import { Alert } from 'antd';
import { InputSelect } from '../Input';
import { CONVERSION_ERROR_CODES, conversionCurrencyOptions, conversionUnitOptions } from '../../utils/conversions';

/**
 * The one control a number <-> measurement conversion needs: the single unit
 * the stored values are in (numeric -> measurement, "Existing values are
 * in") or should become (measurement -> numeric, "Convert stored values
 * to"). Rendered only for a SAVED element whose type is crossing that line
 * (utils/conversions.js planConversion decides; the card editor passes the
 * plan). Writes `{ unit }` under the instruction key through the form's
 * path-based onChange.
 *
 * The API's coded refusals for the conversion — `conversion_unit_required`,
 * `invalid_unit`, and the 409 `backfill_too_large` (the definition was NOT
 * saved) — show here, against the element they name. A client-side block
 * (the save was attempted with no unit) arrives the same way.
 */
/**
 * The CURRENCY variant (docs/plans/currency.md "Conversion") is the same
 * control asking a different question: which of the element's currencies
 * the stored numbers are in (number -> currency, `{ currency }` under
 * `convertFrom`), or whose amounts become plain numbers (currency -> number,
 * under `convertTo`; amounts in any other currency are skipped). Its API
 * refusals are `conversion_currency_required` and `invalid_currency`.
 */
export default function ConversionUnitField({ index, elementName, plan, onChange, saveError = null }) {
	if (!plan) return null;
	if (plan.kind === 'currency') {
		return <ConversionCurrencyField index={index} elementName={elementName} plan={plan} onChange={onChange} saveError={saveError} />;
	}
	const { key, measure, unit } = plan;
	const id = `elements[${index}].options.${key}`;
	const from = key === 'convertFrom';

	const error = saveError && CONVERSION_ERROR_CODES.includes(saveError.code)
		&& (!saveError.element || saveError.element === elementName)
		? saveError
		: null;

	const tooLarge = error?.code === 'backfill_too_large';
	const count = typeof error?.count === 'number' ? error.count.toLocaleString('en-US') : null;
	const cap = typeof error?.cap === 'number' ? error.cap.toLocaleString('en-US') : null;

	return (
		<div className='conversion-unit-field' data-testid={`conversion-${key}`}>
			{error ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message={tooLarge
						? 'Too many items to convert here'
						: (from ? 'Say what unit the existing values are in' : 'Say what unit to convert the stored values to')}
					description={tooLarge
						? (count && cap
							? `${count} items hold a value for this element; the limit for converting on save is ${cap}. The card was not saved — an administrator can run the backfillMeasurement maintenance task instead.`
							: (error.message || 'The card was not saved — an administrator can run the backfillMeasurement maintenance task instead.'))
						: (error.message || null)}
				/>
			) : null}
			<InputSelect
				elementName={id}
				elementLabel={from ? 'Existing values are in' : 'Convert stored values to'}
				value={unit || undefined}
				onChange={(_, unitId) => onChange(id, unitId ? { unit: unitId } : undefined)}
				options={measure ? conversionUnitOptions(measure) : []}
				placeholder={measure ? 'Choose a unit' : 'Choose a measure first'}
				allowClear={false}
				required={true}
				help={from
					? 'The unit the numbers already saved on items were entered in. Saving rewrites each of them as a measurement in that unit; nothing changes meaning.'
					: 'Every stored measurement becomes a plain number in this unit when you save. This is lossy: the unit each value was entered in is forgotten.'}
			/>
		</div>
	);
}

/** The currency conversion's control: see ConversionUnitField. */
function ConversionCurrencyField({ index, elementName, plan, onChange, saveError }) {
	const { key, currencies, currency } = plan;
	const id = `elements[${index}].options.${key}`;
	const from = key === 'convertFrom';

	const error = saveError && CONVERSION_ERROR_CODES.includes(saveError.code)
		&& (!saveError.element || saveError.element === elementName)
		? saveError
		: null;
	const tooLarge = error?.code === 'backfill_too_large';
	const count = typeof error?.count === 'number' ? error.count.toLocaleString('en-US') : null;
	const cap = typeof error?.cap === 'number' ? error.cap.toLocaleString('en-US') : null;

	return (
		<div className='conversion-unit-field' data-testid={`conversion-${key}`}>
			{error ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message={tooLarge
						? 'Too many items to convert here'
						: (from ? 'Say which currency the existing numbers are in' : 'Say which currency to convert to numbers')}
					description={tooLarge
						? (count && cap
							? `${count} items hold a value for this element; the limit for converting on save is ${cap}. The card was not saved — an administrator can run the backfillMeasurement maintenance task instead.`
							: (error.message || 'The card was not saved — an administrator can run the backfillMeasurement maintenance task instead.'))
						: (error.message || null)}
				/>
			) : null}
			<InputSelect
				elementName={id}
				elementLabel={from ? 'Existing numbers are in' : 'Convert amounts in'}
				value={currency || undefined}
				onChange={(_, code) => onChange(id, code ? { currency: code } : undefined)}
				options={conversionCurrencyOptions(currencies)}
				placeholder={currencies?.length ? 'Choose a currency' : 'Choose the currencies first'}
				allowClear={false}
				required={true}
				help={from
					? 'The currency the numbers already saved on items are in. Saving rewrites each as an amount of it, rounded to its decimal places.'
					: 'Amounts in this currency become plain numbers when you save; amounts in any other currency are left out. This is lossy: the currency is forgotten.'}
			/>
		</div>
	);
}
